import { ConfigurationAlert, ConfigurationAlertType, JsonReply, NextLocator, Partaker, SelectOption } from "@grupo-constant/next-dtype/types";
import { ServiceTypology, WorkDayEvent } from "../../services/services.types";
import { LocaleString } from "../../../../core/translate/localeString.model";
import { CompanyUser } from "../../../../core/user/companyUser.model";

export interface PremisesLink {
    oid: string;
    marca_code: string;
    marcaName: string;
    cliente_code: number;
    clienteName: string;
    projectId: string;
    projectName: string;
    serviceId: string;
    serviceName: string;
    premisesType: PremisesType;
    premisesId: string;
    premisesCode: string;
    workplaceOid: string;
    workplaceCode: string;
    stripPremisesCode: string;
    hidden: boolean;
}

export interface PremisesCenterGeneral {
    premisesId: string;
}

export interface PremisesHotelGeneral {
    premisesId: string;
    rooms: number;
    floors: number;
    categoryOid: string;
    typologyOid: string;
    checkIn: string;
    checkOut: string;
    noCleaning: boolean;
    noCleaningBonus: boolean;
    resignationsImpact: number;
    petsAllowed: boolean;
    alert?: ConfigurationAlert[];
    generalRatio: PremisesHotelGeneralRatio;
}

export interface PremisesHotelGeneralRatio {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    ratio: number;
    roomRatio: number;
    weeklyHours: number;
    alert?: ConfigurationAlert[];
}

export interface PremisesHotelOccupancyYearly {
    premisesId: string;
    year: string;
    january: number;
    february: number;
    march: number;
    april: number;
    may: number;
    june: number;
    july: number;
    august: number;
    september: number;
    october: number;
    november: number;
    december: number;
    averageCalculated: number;
    averageManual: number;
    alert?: ConfigurationAlert[];
}

export interface PremisesHotelOccupancyYearlyEvents {
    premisesId: string;
    eventOid: string;
    from: string;
    to: string;
    event: string;
    occupancy: number;
}

export interface PremisesHotelRatio {
    premisesId: string;
    timedActivityType: string;
    timedActivity: string;
    active: boolean;
    ratio: number;
    nonContributeOccupancy: boolean;
    billingType: PremisesHotelRatioBillingTypeEnum;
}

export enum PremisesType {
    CENTER = 'CENTER',
    HOTEL = 'HOTEL',
    LOGISTIC_CENTER = 'LOGISTIC CENTER'
}

export enum PremisesMeasurementType {
    RATIO = 'RATIO',
    TIME = 'TIME',
    BOOLEAN = 'BOOLEAN',
    NUMERIC = 'NUMERIC',
    NOT_APPLY = 'NOT_APPLY'
}

export enum PremisesAllocationScope {
    CENTER = 'CENTER',
    WORKER = 'WORKER'
}

export enum PremisesBillingMode {
    UNITS = 'UNITS',
    RATIO_DERIVED_TIME = 'RATIO_DERIVED_TIME',
    TIME_DERIVED_RATIO = 'TIME_DERIVED_RATIO',
    TIME = 'TIME'
}

export enum PremisesBillingAutomationConcept {
    NONE = 'NONE',
    DAY_HOURS = 'DAY_HOURS',
    NIGHT_HOURS = 'NIGHT_HOURS',
    NIGHT_HOURS_2 = 'NIGHT_HOURS_2',
    HOLIDAY_DAY_HOURS = 'HOLIDAY_DAY_HOURS',
    HOLIDAY_NIGHT_HOURS = 'HOLIDAY_NIGHT_HOURS',
    HOLIDAY_NIGHT_HOURS_2 = 'HOLIDAY_NIGHT_HOURS_2'
}

export interface RoomType {
    roomTypeOid: string;
    premisesId: string;
    name: string;
    abbreviation: string;
    teoricUnits: number;
    inventoryUnits: number;
    capacity: number;
    extraBeds: number;
    adultsInExtraBeds: number;
    childrenInExtraBeds: number;
    airConditioning: boolean;
    heating: boolean;
    housekeeping: boolean;
    optionalHousekeeping: boolean;
    pillowMenu: boolean;
    iron: boolean;
    coffeMaker: boolean;
    minibar: boolean;
    safetyBox: boolean;
    phone: boolean;
    wiredInternet: boolean;
    wirelessInternet: boolean;
    tv: boolean;
    bathtub: boolean;
    bathrobe: boolean;
    showerTray: boolean;
    slippers: boolean;
    bidet: boolean;
    hairdryer: boolean;
    jacuzzi: boolean;
    connectingRooms: boolean;
    roomCatalogActivities: RoomActivity[];
    alert?: ConfigurationAlert[];
}

export interface RoomActivity {
    premisesId: string;
    roomTypeOId: string;
    serviceId: string;
    workplaceOid: string;
    billingType: PremisesActivityTypeEnum;
    timedActivity: string;
    timedCalculated: number;
    timed: number;
    net4Concepts: SearchNet4Concept[];
}

export interface CommonAreaType {
    type: string;
    name: string;
    teoricUnits: number;
    inventoryUnits: number;
}

export interface SearchNet4Concept {
    net4ConceptId: number;
    net4ConceptName: string;
}

export interface PremisesCenterActivityType {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    net4ConceptId: number;
    net4OriginalConceptName: string;
    net4ConceptName: string;
    supervision: boolean;
    notBillable: boolean;
    activityCategory: string;
    type: PremisesActivityTypeEnum;
    subtype: string;
    measurementUnit: string;
    timed: number | null;
    ratioAsTime: boolean;
    accumulated4Period: boolean;
    valueIsOne: boolean;
    plateRequirementLevel: PremisesRequirementLevel;
    isPlatePictureMandatory: boolean;
    baseRequirementLevel: PremisesRequirementLevel;
    bases: string[];
    escort: boolean;
    escortNoBilling: boolean;
    escortPlateOptional: boolean;
    showAsRecentActivity: boolean;
    showAsRecentPlate: boolean;
    isPlateUnique: boolean;
    jobs: string[];
    deprecated: boolean;
    withName: string[];
}

export enum PremisesRequirementLevel {
    MANDATORY = 'MANDATORY',
    OPTIONAL = 'OPTIONAL',
    NEVER = 'NEVER'
}

export interface PremisesHotelActivity {
    premisesId: string;
	serviceId: string;
	workplaceOid: string;
	net4ConceptId: number;
	net4ConceptName: string;
    net4OriginalConceptName: string;
    shortName: string;
	type: PremisesActivityTypeEnum;
    subtype: PremisesActivitySubtypeEnum;
	jobs: string[];
    supervision: boolean;
    accumulated4Period: boolean;
	discount: boolean;
	notBillable: boolean;
	nestIn: string;
	deprecated: boolean;
}

export interface PremisesActivityType {
    premisesId: string;
	serviceId: string;
	workplaceOid: string;
	net4ConceptId: number;
	net4ConceptName: string;
    net4OriginalConceptName: string;
    shortName: string;
	type: PremisesActivityTypeEnum;
    subtype: PremisesActivitySubtypeEnum;
	jobs: string[];
    supervision: boolean;
    accumulated4Period: boolean;
	discount: boolean;
	notBillable: boolean;
	nestIn: string;
	deprecated: boolean;
}

export interface PremisesGenericActivityType {
    //IDENTIDAD
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    period: string;
    year: string;
    net4ConceptId: number;
    net4OriginalConceptName: string;
    net4ConceptName: string;
    displayName: string;
    activityCategory: string;
    groupName: string;
    notBillable: boolean;
    supervision: boolean;
    //MEDICIÓN
    measurementType: PremisesMeasurementType;
    measurementUnit: string;
    requestTimeAsMinutes: boolean;
    accumulated4Period: boolean;
    discount: boolean;
    allowMoreSections: boolean;
    //IMPUTACIÓN
    allocationScope: PremisesAllocationScope;
    requiresTimeTrackingForRatioActivities: boolean;
    requiresTimeTracking: boolean;
    //FACTURACIÓN
    billingMode: PremisesBillingMode;
    billingAutomation: string;
    billingAutomationConcept: PremisesBillingAutomationConcept[];
    unitsRange: boolean;
    minUnits: number;
    targetUnits: number;
    timed: number;
    timeRelativeToTarget: boolean;
    //DESGLOSE OPERATIVO
    alternativeNames?: string[];
    //GESTIÓN DE LA DEMANDA
    demandPeriod: PremisesDemandPeriodEnum;
    dailyDemand: PremisesDailyDemand[];
    deprecated: boolean;
}

export interface PremisesGenericActivityTypeObjective {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    workplaceCode: string;
    dailyDemandId?: string;
    objectiveId?: string;
    date: string;
    net4ConceptId?: number;
    net4ConceptAlternativeName?: string;
    billingAutomation?: string;
    fromTime?: string;
    toTime?: string;
    workdaySplitMinutes?: number;
    workdaySplitFromTime?: string;
    premisesDailyDemandType: PremisesDailyDemandTypeEnum;
    goal?: number;
    minGoal?: number;
    plannedGoal?: number;
    realGoal?: number;
    disconnectedFromDailyDemand: boolean;
    deprecatedAsDailyDemand: boolean;
}

export interface PremisesHotelActivityGeneral {
    premisesId: string;
    shortName4RoomRatio: string;
    shortName4CommonArea: string;
    alert?: ConfigurationAlert[];
}

export enum PremisesHotelRatioBillingTypeEnum {
    RATIO = 'RATIO',
    TIME = 'TIME',
}

export enum PremisesActivityTypeEnum {
    RATIO = 'RATIO',
    TIME = 'TIME',
    OTHER = 'OTHER',
    NOT_APPLY = 'NOT_APPLY'
}

export enum PremisesActivitySubtypeEnum {
    BOOLEAN = 'BOOLEAN',
    NUMERIC = 'NUMERIC'
}

export interface PremisesCenterActivityCategory {
    premisesId: string;
    activityCategoryOid: string;
    name: string;
    deprecated: boolean;
}

export interface PremisesCenterBase {
    premisesId: string;
    baseOid: string;
    name: string;
    deprecated: boolean;
}

export interface PremisesHotelTip {
    premisesId: string;
    tipOid?: string
	timedActivityType: string;
    name: string;
	whenApplies?: PremisesHotelTipWhenAppliesEnum;
    valueWhenApplies?: string;
    deprecated: boolean;
}

export enum PremisesHotelTipWhenAppliesEnum {
    ROOM_STARTS_WITH = 'ROOM_STARTS_WITH'
}

export interface PremisesHotelShiftGeneral {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    dayStartsAt: string;
}

export interface PremisesShift {
    premisesId: string;
	serviceId: string;
	workplaceOid: string;
    shiftOid: string;
	name: string;
	weekDays: WeekDaysEnum[];
    deprecatedFrom: string;
	deprecatedTo: string;
}

export interface PremisesGenericShift {
    premisesId: string;
	serviceId: string;
	workplaceOid: string;
    shiftOid: string;
	name: string;
	weekDays: WeekDaysEnum[];
    deprecatedFrom: string;
	deprecatedTo: string;
}

export interface PremisesShiftDailyWork {
    shiftOid: string;
	name: string;
	fromTime: string;
	toTime: string;
	weekDays: WeekDaysEnum[];
    deprecatedFrom: string;
	deprecatedTo: string;
    schedules: PremisesShiftSchedule[];
}

export enum WeekDaysEnum {
    MONDAY = 'MONDAY',
    TUESDAY = 'TUESDAY',
    WEDNESDAY = 'WEDNESDAY',
    THURSDAY = 'THURSDAY',
    FRIDAY = 'FRIDAY',
    SATURDAY = 'SATURDAY',
    SUNDAY = 'SUNDAY'
}

export interface PremisesShiftSchedule {
    premisesId: string;
	serviceId: string;
	workplaceOid: string;
    shiftOid: string;
	scheduleOid: string;
    name?: string;
	fromTime: string;
	toTime: string;
    tip: string[];
    base: string[];
	activityName: string[];
    isDefault: boolean;
}

export interface PremisesGenericShiftSchedule {
    premisesId: string;
	serviceId: string;
	workplaceOid: string;
    shiftOid: string;
	scheduleOid: string;
    name?: string;
	fromTime: string;
	toTime: string;
    groupNames: string[];
    isDefault: boolean;
}

export enum PremisesShiftScheduleProrataBreak {
    NO = 'NO',
    ROUND_1_MINUTES = 'ROUND_1_MINUTES',
    ROUND_5_MINUTES = 'ROUND_5_MINUTES',
    ROUND_10_MINUTES = 'ROUND_10_MINUTES'
}

export interface PremisesHotelClosingConcept {
    premisesId: string;
    closingConceptOid?: string;
    closingConcept: string;
    occupancyContribution: OccupancyContributionEnum;
    dirtyPreviousDay: boolean;
    dirtyNextDay: boolean;
    numbersOfRoom: boolean;
    noCustom: boolean;
    remark: string;
}

export enum OccupancyContributionEnum {
    SUBTRACTION = 'SUBTRACTION',
    ADDITION = 'ADDITION',
    EQUAL = 'EQUAL'
}

export interface PremisesHotelDaily {
    marca_code: string;
    cliente_code: number;
    projectId: string;
    serviceId: string;
    workplaceOid: string;
    workplaceCode: string;
    premisesType: string;
    premisesId: string;
    premisesCode: string;
    date: string;
    dayOfMonth: string;
    period: string;
    year: string;
    weekOfYear: number;
    startDayTime: string;
    expectedOccupancy: number;
    expectedOccupancyPercent: number;
    occupancyRooms: number;
    departureRooms: number;
    ratio: number;
    plannedRatio: number;
    finalOccupancy: number;
    roomsDone: number;
    timestamp: string;
    alertType: ConfigurationAlertType;
    alert: ConfigurationAlert[];
    activityPayload;
}

export interface CenterWorkCurrentStatus {
    date: string;
    modified: boolean;
    assigned: number;
    unfilled: number;
    status: PremisesCenterWorkStatusEnum;
    statusDateTime: string;
}

export interface GenericWorkCurrentStatus {
    date: string;
    modified: boolean;
    assigned: number;
    unfilled: number;
    status: PremisesGenericWorkStatusEnum;
    statusDateTime: string;
}

export interface HotelWorkCurrentStatus {
    date: string;
    modified: boolean;
    assigned: number;
    unfilled: number;
    status: PremisesHotelWorkStatusEnum;
    statusDateTime: string;
    pdfUrl?: string;
}

export interface CenterDailyTask {
    dailyTaskId: string;
    parentDailyTaskId: string;
    typology: ServiceTypology;
    externalDailyTaskId: string;
    marca_code: string;
    cliente_code: number;
    projectId: string;
    serviceId: string;
    workplaceOid: string;
    workplaceCode: string;
    premisesType: string;
    premisesId: string;
    premisesCode: string;
    zombieNumber: number;
    zombieName: string;
    companyId: string;
    empresaId: number;
    empresaName: string;
    userId: string;
    userSAMAccountName: string;
    displayName: string;
    userJob: string;
    weeklyHours: number;
    plannedShiftName: string;
    plannedShiftOid: string;
    scheduled_from_date: string;
    scheduled_from_time: string;
    scheduled_to_time: string;
    weekOfYear: number;
    dailyMinutes: number;
    breakMinutes: number;
    overwrittenBreakMinutes: number;
    overwrittenDailyMinutes: number;
    activities: DailyTaskActivity[];
    withHourlyActivities: boolean;
    withRatioActivities: boolean;
    plannedTips: string[];
    expectedRatio: number;
    expectedMinutes: number;
    ratio: number;
    plannedRatio: number;
    plannedMinutes: number;
    minutes: number;
    shiftName: string;
    shiftOid: string;
    tips: string[];
    plannedStartDailyTaskDate: string;
    plannedEndDailyTaskDate: string;
    plannedBreakMinutes: number;
    firstShowStartDailyTaskDate: string;
    lastShowEndDailyTaskDate: string;
    showBreakMinutes: number;
    workedStartDailyTaskDate: string;
    workedEndDailyTaskDate: string;
    workedBreakMinutes: number;
    customStartDailyTaskDate: string;
    customEndDailyTaskDate: string;
    customBreakMinutes: number;
    validatorSelection: ValidatorSelectionEnum;
    status: DailyTaskStatusEnum;
    customStatus: string;
    timestamp: string;
    dayOff: boolean;
    reassigned: boolean;
    assignmentError: boolean;
    alertType: ConfigurationAlertType;
    alert: ConfigurationAlert[];
    absencePayload: AbsencePayloadItem[];
    plannedAbsence: PlannedAbsence;
    events: DailyTaskEvent[];
    statusInConnectCore: string;
}

export interface DailyTask {
    dailyTaskId: string;
    parentDailyTaskId: string;
    typology: ServiceTypology;
    externalDailyTaskId: string;
    marca_code: string;
    cliente_code: number;
    projectId: string;
    serviceId: string;
    workplaceOid: string;
    workplaceCode: string;
    premisesType: string;
    premisesId: string;
    premisesCode: string;
    zombieNumber: number;
    zombieName: string;
    companyId: string;
    empresaId: number;
    empresaName: string;
    userId: string;
    userSAMAccountName: string;
    displayName: string;
    userJob: string;
    weeklyHours: number;
    plannedShiftName: string;
    plannedShiftOid: string;
    plannedShiftScheduledOid: string;
    plannedShiftScheduledName: string;
    scheduled_from_date: string;
    scheduled_from_time: string;
    scheduled_to_time: string;
    weekOfYear: number;
    dailyMinutes: number;
    breakMinutes: number;
    overwrittenBreakMinutes: number;
    overwrittenDailyMinutes: number;
    activities: DailyTaskActivity[];
    withHourlyActivities: boolean;
    withRatioActivities: boolean;
    plannedTips: string[];
    expectedRatio: number;
    expectedMinutes: number;
    ratio: number;
    plannedRatio: number;
    plannedMinutes: number;
    minutes: number;
    shiftName: string;
    shiftOid: string;
    tips: string[];
    plannedStartDailyTaskDate: string;
    plannedEndDailyTaskDate: string;
    plannedBreakMinutes: number;
    plannedWorkDaySplitMinutes: number;
    plannedWorkdaySplitFromTime: string;
    firstShowStartDailyTaskDate: string;
    lastShowEndDailyTaskDate: string;
    showBreakMinutes: number;
    workedStartDailyTaskDate: string;
    workedEndDailyTaskDate: string;
    workedBreakMinutes: number;
    workedWorkDaySplitMinutes: number;
    customStartDailyTaskDate: string;
    customEndDailyTaskDate: string;
    customBreakMinutes: number;
    customWorkDaySplitMinutes: number;
    validatorSelection: ValidatorSelectionEnum;
    status: DailyTaskStatusEnum;
    customStatus: string;
    timestamp: string;
    dayOff: boolean;
    reassigned: boolean;
    assignmentError: boolean;
    dailyTaskPayload: DailyTaskPayload;
    alertType: ConfigurationAlertType;
    alert: ConfigurationAlert[];
    absencePayload: AbsencePayloadItem[];
    absentAllDay: boolean;
    plannedAbsence: PlannedAbsence;
    events: DailyTaskEvent[];
    statusInConnectCore: string;
    paidRest: boolean;
    legalBreak?: DailyTaskLegalBreak[];
}

export interface DailyTaskLegalBreak {
    fromMinutes: number;
    breakMinutes: number;
}

export interface DailyTaskPayload {
    creationDate: string;
    lastUpdateDate: string;
    typology?: ServiceTypology;
    premisesType?: PremisesType;
    roomWorkRatio?: RoomWorkRatio[];
    workRatioActivity?: WorkRatioActivity[];
    activityWorktime?: ActivityWorktime[];
    otherActivity?: OtherActivity[];
    closingConceptData?: ClosingConceptData[];
    genericBillingAutomationDisabled?: boolean;
    genericBillingAutomation?: string;
    genericPlannedBillingAutomation?: string;
    genericOtherActivity?: GenericOtherActivity[];
    genericWorkRatioActivity?: GenericWorkRatioActivity[];
    genericActivityWorktime?: GenericActivityWorktime[];
    genericBillingAutomationGroup?: GenericBillingAutomationGroup[];
}

export interface DailyTaskEvent {
    checkIn: boolean;
    checkOut: boolean;
    queueTimestamp: string;
    receivedTimestamp: string;
}

export interface WeeklyWorkSummaryRow {
    userId: string;
    userSAMAccountName: string;
    userJob: string;
    displayName: string;
    weekHours: number;
    absentAllWeek: boolean;
    empresaId: number;
    dayCell: DailyTask[];
}

export interface RoomWorkRatio {
    abbreviation: string;
    activities: {
        activity: string;
        billingType: PremisesHotelRatioBillingTypeEnum;
        quantity: number;
        net4Concept: SearchNet4Concept[];
        timed: number;
        nonContributeOccupancy: boolean;
    }[];
    name: string;
    roomTypeOid: string;
}

export interface WorkRatioActivity {
    net4ConceptId: number;
    net4ConceptName: string;
    timed: number;
    quantity: number;
    ratioAsTime: boolean;
    payload?: ActivityPayload;
}

export interface GenericWorkRatioActivity {
    net4ConceptId: number;
    net4ConceptName: string;
    alternativeName: string | null;
    displayName: string;
    activityCategory: string;
    groupName: string;
    measurementType: PremisesMeasurementType; // RATIO
    allocationScope: PremisesAllocationScope;
    billingMode: PremisesBillingMode;
    notBillable: boolean;
    plannedItems: GenericWorkRatioActivityItem[];
    items?: GenericWorkRatioActivityItem[];
    objectiveProgress?: GenericObjectiveProgress;
}

export interface GenericWorkRatioActivityItem {
    planned?: number;
    realized?: number;
    ratio: number;
    time?: number;
    fromTime?: string;
    toTime?: string;
    theoricRatio: number;
    theoricTime: number;
}

export interface ActivityWorktime {
    net4ConceptId: number;
    net4ConceptName: string;
    plannedMinutes: number;
    shortName: string;
    workedMinutes: number;
    ratioAsTime: boolean;
    payload?: ActivityPayload;
}

export interface GenericActivityWorktime {
    net4ConceptId: number;
    net4ConceptName: string;
    alternativeName: string | null;
    displayName: string;
    activityCategory: string;
    groupName: string;
    measurementType: PremisesMeasurementType; // TIME
    allocationScope: PremisesAllocationScope;
    billingMode: PremisesBillingMode;
    notBillable: boolean;
    supervision: boolean;
    billingAutomation?: string;
    billingAutomationConcept?: PremisesBillingAutomationConcept[];
    plannedItems: GenericActivityWorktimeItem[];
    items?: GenericActivityWorktimeItem[];
    objectiveProgress?: GenericObjectiveProgress;
}

export interface GenericActivityWorktimeItem {
    planned?: number;
    realized?: number;
    time?: number;
    fromTime?: string;
    toTime?: string;
    theoricRatio: number;
}

export interface ActivityPayload {
    numActivities: number;
    numActivitiesWithAlerts: number;
}

export interface OtherActivity {
    net4ConceptId: number;
    net4ConceptName: string;
    subtype: PremisesActivitySubtypeEnum;
    value: number;
}

export interface GenericOtherActivity {
    net4ConceptId: number;
    net4ConceptName: string;
    alternativeName: string | null;
    displayName: string;
    activityCategory: string;
    groupName: string;
    measurementType: PremisesMeasurementType; // BOOLEAN & NUMERIC
    allocationScope: PremisesAllocationScope;
    billingMode: PremisesBillingMode;
    notBillable: boolean;
    plannedValue: number;
    value?: number;
}

export interface GenericBillingAutomationGroup {
    billingAutomation?: string;
    displayName: string;
    items: GenericBillingAutomationGroupItem[];
    objectiveProgress?: GenericObjectiveProgress;
}

export interface GenericBillingAutomationGroupItem {
    time: number;
    fromTime?: string;
    toTime?: string;
    goal?: number;
    minGoal?: number;
}

export interface GenericActivityForInterface {
    net4ConceptId: number;
    net4ConceptName: string;
    activityCategory: string;
    groupName: string;
    measurementType: PremisesMeasurementType;
    allocationScope: PremisesAllocationScope;
    billingMode: PremisesBillingMode;

    // OTHER
    value?: number;

    // RATIO
    ratioItems?: GenericWorkRatioActivityItem[];

    // TIME
    timeItems?: GenericActivityWorktimeItem[];
}

export interface GenericObjectiveProgress {
    plannedObjectivePercentage?: number;
    plannedCoveragePercentage?: number;
    realObjectivePercentage?: number;
    realCoveragePercentage?: number;
}

export interface ClosingConceptData {
    closingConcept: string;
    closingConceptOid: string;
    isNumbersOfRoom: boolean;
    occupancyContribution: OccupancyContributionEnum;
    numbersOfRoom?: string[];
    quantity?: number;
    noCustom: boolean;
}

export interface CustomClosingConceptData extends ClosingConceptData {
    active: boolean;
}

export interface DailyTaskActivity {
    activity: string;
    type: PremisesActivityTypeEnum;
    minutes: number;
}

export interface DailyTaskActivityView extends DailyTaskActivity {
    checked: boolean;
    net4ConceptId?: number;
}

export enum DailyTaskStatusEnum {
    CREATED = 'CREATED',
    WITHERROR = 'WITHERROR',
    PLANNED = 'PLANNED',
    CANCELLED = 'CANCELLED',
    WORKING = 'WORKING',
    PAUSED = 'PAUSED',
    DONE = 'DONE',
    DONE_WITH_ERROR = 'DONE_WITH_ERROR',
    DELETED = 'DELETED'
}

export enum ValidatorSelectionEnum {
    PLANNED = 'PLANNED',
    WORKED = 'WORKED',
    CUSTOM = 'CUSTOM',
    SHOW = 'SHOW',
    NONE = 'NONE'
}

export interface AbsencePayloadItem {
    type: string;
    date: string;
    fromTime?: string;
    toTime?: string;
    wholeDay: boolean;
    confirmed: boolean;
    locked: boolean;
    formOid?: string;
}

export interface PlannedAbsence {
    dayOff: boolean;
    reassigned: boolean;
    assignmentError: boolean;
    absencePayload: AbsencePayloadItem[];
}

export interface AbsenceType {
    _oid: string;
    abbreviation: string;
    name: LocaleString;
    isDateInterval: boolean;
    isDocumentsRequired: boolean;
    isHourInterval: boolean;
    isUserOption: boolean;
    _deprecated: boolean;
    color: string;
    darkColor: string;
}

export interface PremisesHotelMagicCode {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    magicCode: string;
    creationDate: string;
    expirationDate: string;
    consumedDeviceUUID: string;
    consumedDate: string;
}

export interface PremisesCenterMagicCode {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    magicCode: string;
    creationDate: string;
    expirationDate: string;
    consumedDeviceUUID: string;
    consumedDate: string;
}

export interface PremisesHotelContact {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    contactOid: string;
    contactType: ContactTypeEnum;
    displayName: string;
    mail: string;
    remarks: string;
}

export interface PremisesGenericContact {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    contactOid: string;
    contactType: PremisesGenericContactTypeEnum;
    displayName: string;
    mail: string;
    remarks: string;
}

export enum ContactTypeEnum {
    CC = 'CC',
    CCO = 'CCO',
    COORDINADOR_DE_OPERACIONES = 'COORDINADOR_DE_OPERACIONES',
    APROBADOR_CLIENTE = 'APROBADOR_CLIENTE',
    BACKOFFICE = 'BACKOFFICE',
    CSC = 'CSC',
    GOBERNANTA = 'GOBERNANTA',
    SUBGOBERNANTA = 'SUBGOBERNANTA'
}

export enum PremisesGenericContactTypeEnum {
    CC = 'CC',
    CCO = 'CCO',
    COORDINADOR_DE_OPERACIONES = 'COORDINADOR_DE_OPERACIONES',
    APROBADOR_CLIENTE = 'APROBADOR_CLIENTE',
    BACKOFFICE = 'BACKOFFICE',
    CSC = 'CSC',
    GOBERNANTA = 'GOBERNANTA',
    SUBGOBERNANTA = 'SUBGOBERNANTA'
}

export interface PremisesCenterWork {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    workplaceCode: string;
    date: string;
    status: PremisesCenterWorkStatusEnum;
    statusDateTime: string;
    modified: true;
    clientRemarks: string;
    payload: PremisesCenterWorkPayload;
}

export interface PremisesGenericWork {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    workplaceCode: string;
    date: string;
    status: PremisesGenericWorkStatusEnum;
    statusDateTime: string;
    imputationUserId?: string;
    imputationUserDisplayName?: string;
    modified: true;
    clientRemarks: string;
    payload: PremisesGenericWorkPayload;
}

export interface PremisesHotelWork {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    workplaceCode: string;
    date: string;
    wordUrl: string;
    pdfUrl: string;
    modified: true;
    creationTime: string;
    sendTime: string;
    receivedTime: string;
    approverUserId: string;
    approverSAMAccountName: string;
    approverDisplayName: string;
    status: PremisesHotelWorkStatusEnum;
    statusDateTime: string;
    generatorUserId: string;
    generatorSAMAccountName: string;
    generatorDisplayName: string;
    payload: PremisesHotelWorkPayload;
    clientRemarks: string;
}

export enum PremisesCenterWorkStatusEnum {
    SENT = 'SENT',
    PENDING = 'PENDING',
    ERROR = 'ERROR'
}

export enum PremisesGenericWorkStatusEnum {
    SENT = 'SENT',
    PENDING = 'PENDING',
    ERROR = 'ERROR'
}

export enum PremisesHotelWorkStatusEnum {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    SENT = 'SENT',
    PENDING = 'PENDING',
    DOCUMENT = 'DOCUMENT',
    ERROR = 'ERROR',
    ERROR_WITHOUT_APPROVERS = 'ERROR_WITHOUT_APPROVERS'
}

export interface PremisesCenterWorkStatus {
    status: PremisesCenterWorkStatusEnum;
    statusDateTime: string;
    remarks: string;
    userId: string;
    sAMAccountName: string;
    displayName: string;
}

export interface PremisesGenericWorkStatus {
    status: PremisesGenericWorkStatusEnum;
    statusDateTime: string;
    remarks: string;
    userId: string;
    sAMAccountName: string;
    displayName: string;
}

export interface PremisesHotelWorkStatus {
    status: PremisesHotelWorkStatusEnum;
    statusDateTime: string;
    remarks: string;
    mailRecipients: MailRecipients[];
    userId: string;
    sAMAccountName: string;
    displayName: string;
}

export interface MailRecipients {
    to: MailRecipient;
    cc: MailRecipient[];
    bcc: MailRecipient[];
}

export interface MailRecipient {
    name: string;
    mail: string;
}

export interface PremisesCenterWorkPayload {
    summary: CenterWorkSummary;
    otherActivity: OtherActivity[];
    activityWorktime: ActivityWorktime[];
    workRatioActivity: WorkRatioActivity[];
    numActivities: number;
    numActivitiesWithAlerts: number;
}

export interface PremisesGenericWorkPayload {
    summary: CenterWorkSummary;
    plannedSummary: GenericPlannedWorkSummary;
    genericOtherActivity: GenericOtherActivity[];
    genericWorkRatioActivity: GenericWorkRatioActivity[];
    genericActivityWorktime: GenericActivityWorktime[];
    genericBillingAutomationGroup?: GenericBillingAutomationGroup[];
}

export interface GenericPlannedWorkSummary {
    general: PlannedWorkSummaryGeneral;
}

export interface PlannedWorkSummaryGeneral {
    assigned: number;
    dayOff: number;
    reassigned: number;
    assignmentError: number;
    absentAllDay: number;
    withPlanning: number;
    withoutPlanning: number;
    plannedMinutes: number;
    partialAbsencesMinutes: number;
}

export interface PremisesHotelWorkPayload {
    summary: HotelWorkSummary;
    roomWorkRatio: RoomWorkRatio[];
    activityWorktime: ActivityWorktime[];
    closingConceptData: ClosingConceptData[];
    customClosingConceptData: CustomClosingConceptData[];
}

export interface CenterWorkSummary {
    general: WorkSummaryGeneral;
    activityWorktime: WorkSummaryActivityWorktime[];
    workRatioActivity: WorkRatioActivity[];
    totalByNet4Concept: TotalByNet4Concept[];
}

export interface HotelWorkSummary {
    occupancy: HotelWorkSummaryOccupancy;
    general: WorkSummaryGeneral;
    roomWorkRatio: HotelWorkSummaryRoomWorkRatio[];
    activityWorktime: WorkSummaryActivityWorktime[];
    closingConceptData: HotelWorkSummaryClosingConceptData[];
    closingConceptDataAddition: number;
    closingConceptDataSubtraction: number;
    totalByNet4Concept: TotalByNet4Concept[];
}

export interface HotelWorkSummaryOccupancy {
    rooms: number;
    estimatedOccupancy: number;
    departureRooms: number;
    actualOccupancy: number;
    roomsDone: number;
    finalOccupancy: number;
    diff: number;
}

export interface WorkSummaryGeneral {
    assigned: number;
    dayOff: number;
    reassigned: number;
    assignmentError: number;
    absentAllDay: number;
    withWork: number;
    unfilled: number;
    assignedMinutes: number;
    partialAbsencesMinutes: number;
}

export interface HotelWorkSummaryRoomWorkRatio {
    roomAbbreviation: string;
    activities: HotelWorkSummaryRoomWorkRatioActivity[];
    totalRatio: number;
}

export interface HotelWorkSummaryRoomWorkRatioActivity {
    activity: string;
    billingType: PremisesActivityTypeEnum;
    quantity: number;
}

export interface WorkSummaryActivityWorktime {
    shortName: string;
    ratioAsTime: boolean;
    workedMinutes: number;
}

export interface HotelWorkSummaryClosingConceptData {
    closingConcept: string;
    units: number;
    occupancy: number;
}

export interface TotalByNet4Concept {
    net4ConceptId: number;
    net4ConceptName: string;
    quantity: number;
    ratio: number;
    time: number;
    ratioAsTime: boolean;
    timeAsRatio: boolean;
    type: PremisesMeasurementType;
}

export interface PremisesGenericGroups {
    premisesId: string;
    activityCategory: string[];
    groupDisplayName: string;
    groupNames: string[];
    billingAutomation: string[];
}

export interface PremisesGenericActivityTypeObjectiveListUpdate {
    serviceId: string;
    premisesId: string;
    workplaceOid: string;
    date: string;
    goalSource: 'PLANNED' | 'REAL';
    objectives: PremisesGenericActivityTypeObjective[];
}

export enum DailyTaskWorkedHoursStatusEnum {
    PENDING = 'PENDING',
    VALIDATED = 'VALIDATED'
}

export enum ConnectCoreDailyWorkStatusEnum {
    PENDING = 'PENDING',
    TRANSFERRED = 'TRANSFERRED',
    DELETED = 'DELETED',
    ERROR = 'ERROR'
}

export interface RetrievePremisesLinkListRequest {
    locator: NextLocator;
    expr: string;
    includeHidden: boolean;
    chunksize: number;
    lastConsumedOid?: string;
}

export interface RetrievePremisesLinkRequest {
    locator: NextLocator;
    oid: string;
}

export interface ChangePremisesLinkStatusRequest {
    marca_code: string;
    premisesLinkOid: string;
    hidden: boolean;
}

export interface RetrievePremisesCenterGeneralRequest {
    marca_code: string;
    premisesId: string;
}

export interface RetrievePremisesHotelGeneralRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
}

export interface FlushPremisesCenterGeneralRequest {
    marca_code: string;
    general: PremisesCenterGeneral;
}

export interface FlushPremisesHotelGeneralRequest {
    marca_code: string;
    general: PremisesHotelGeneral;
}

export interface RetrievePremisesHotelOccupancyYearlyRequest {
    marca_code: string;
    premisesId: string;
    year: string;
}

export interface FlushPremisesHotelOccupancyYearlyRequest {
    marca_code: string;
    occupancyYearly: PremisesHotelOccupancyYearly;
}

export interface DeletePremisesHotelOccupancyEventsRequest {
    marca_code: string;
    eventOid: string;
}

export interface RetrievePremisesHotelOccupancyYearlyEventsRequest {
    marca_code: string;
    premisesId: string;
    year: string;
}

export interface FlushPremisesHotelOccupancyYearlyEventsRequest {
    marca_code: string;
    event: PremisesHotelOccupancyYearlyEvents;
}

export interface RetrievePremisesHotelRatioRequest {
    marca_code: string;
    premisesId: string;
}

export interface FlushPremisesHotelRatioRequest {
    marca_code: string;
    ratio: PremisesHotelRatio;
}

export interface RetrievePremisesHotelNet4ConceptsRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    workplaceCode: string;
    year: string;
    period: string;
    includeDeprecated: boolean;
    synchronize: boolean;
}

export interface CleanPremisesGenericNet4ConceptsRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    workplaceCode: string;
    year: string;
    period: string;
    includeDeprecated: boolean;
    synchronize: boolean;
}

export interface SearchPremisesHotelNet4ConceptsRequest {
    marca_code: string;
    premisesId: string;
    workplaceOid: string;
    expr: string;
    includeDeprecated: boolean;
}

export interface SearchPremisesHotelActivityNet4ConceptsRequest {
    marca_code: string;
    premisesId: string;
    workplaceOid: string;
    serviceId: string;
    expr: string;
    billingType: PremisesActivityTypeEnum;
    includeDeprecated: boolean;
}

export interface RetrievePremisesHotelRoomCatalogListRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
}

export interface FlushPremisesHotelRoomCatalogRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    roomCatalog: RoomType;
}

export interface DeletePremisesHotelRoomCatalogRequest {
    marca_code: string;
    premisesId: string;
    roomTypeOid: string[];
}

export interface RetrievePremisesActivityListRequest {
    marca_code: string;
    premisesId: string;
	serviceId: string;
	workplaceOid: string;
	includeDeprecated: boolean;
}

export interface RetrievePremisesGenericActivityTypeListRequest {
    marca_code: string;
    premisesId: string;
	serviceId: string;
	workplaceOid: string;
    period: string;
    year: string;
	includeDeprecated: boolean;
}

export interface FlushPremisesCenterActivityRequest {
    marca_code: string;
    activity: PremisesCenterActivityType;
}

export interface FlushPremisesHotelActivityRequest {
    marca_code: string;
    activity: PremisesHotelActivity;
}

export interface FlushPremisesGenericActivityTypeRequest {
    marca_code: string;
    activityType: PremisesGenericActivityType;
}

export interface FlushPremisesActivityTypeOrderRequest {
    marca_code: string;
    premisesId: string;
	serviceId: string;
	workplaceOid: string;
    period?: string;
    year?: string;
	net4ConceptId: number[];
}

export interface FlushPremisesGenericWorkRealizedConceptRequest {
    date: string;
    premisesId: string;
	serviceId: string;
    net4ConceptId: number;
    measurementType: PremisesMeasurementType;
    alternativeName?: string;
    realized: number;
}

export interface RetrievePremisesHotelActivityGeneralRequest {
    marca_code: string;
    premisesId: string;
	serviceId: string;
	workplaceOid: string;
	includeDeprecated: boolean;
}

export interface FlushPremisesHotelActivityGeneralRequest {
    marca_code: string;
    activityGeneral: PremisesHotelActivityGeneral;
}

export interface RetrievePremisesCenterBaseListRequest {
    marca_code: string;
    premisesId: string;
    includeDeprecated: boolean;
}

export interface FlushPremisesCenterBaseRequest {
    marca_code: string;
    base: PremisesCenterBase;
}

export interface DeletePremisesCenterBaseListRequest {
    marca_code: string;
    premisesId: string;
    baseOid: string[];
}

export interface RetrievePremisesCenterBaseListReply extends JsonReply {
    bases: PremisesCenterBase[];
}

export interface FlushPremisesCenterBaseReply extends JsonReply {
    base: PremisesCenterBase;
}

export interface RetrievePremisesCenterActivityCategoryListRequest {
    marca_code: string;
    premisesId: string;
    includeDeprecated: boolean;
}

export interface FlushPremisesCenterActivityCategoryRequest {
    marca_code: string;
    activityCategory: PremisesCenterActivityCategory;
}

export interface DeletePremisesCenterActivityCategoryListRequest {
    marca_code: string;
    premisesId: string;
    activityCategoryOid: string[];
}

export interface RetrievePremisesCenterActivityCategoryListRequest {
    marca_code: string;
    premisesId: string;
    includeDeprecated: boolean;
}

export interface RetrievePremisesCenterActivityCategoryListReply extends JsonReply {
    activityCategories: PremisesCenterActivityCategory[];
}

export interface FlushPremisesCenterActivityCategoryReply extends JsonReply {
    activityCategory: PremisesCenterActivityCategory;
}

export interface RetrievePremisesHotelTipListRequest {
    marca_code: string;
    premisesId: string;
    includeDeprecated: boolean;
}

export interface FlushPremisesHotelTipRequest {
    marca_code: string;
    tip: PremisesHotelTip;
}

export interface DeletePremisesHotelTipListRequest {
    marca_code: string;
    premisesId: string;
    tipOid: string[];
}

export interface RetrievePremisesHotelShiftGeneralRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
}

export interface FlushPremisesHotelShiftGeneralRequest {
    marca_code: string;
    shiftGeneral: PremisesHotelShiftGeneral;
}

export interface RetrievePremisesHotelShiftListRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
}

export interface RetrievePremisesGenericShiftListRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
}

export interface FlushPremisesHotelShiftRequest {
    marca_code: string;
    shift: PremisesShift;
    schedule: PremisesShiftSchedule[];
}

export interface FlushPremisesGenericShiftRequest {
    marca_code: string;
    shift: PremisesGenericShift;
    schedule: PremisesGenericShiftSchedule[];
}

export interface DeletePremisesHotelShiftListRequest {
    marca_code: string;
    shiftOid: string[];
}

export interface RetrievePremisesHotelShiftScheduleListRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    shiftOid: string;
}

export interface RetrievePremisesGenericShiftScheduleListRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    shiftOid: string;
}

export interface RetrievePremisesDailyWorkRequest {
    serviceId: string;
    premisesId: string;
    date: string;
}

export interface FlushPremisesHotelDailyOccupancyRequest {
    serviceId: string;
    premisesId: string;
    date: string;
    occupancyRooms: number;
    departureRooms: number;
}

export interface RetrievePremisesDailyWorkSearchOptionsRequest {
    serviceId: string;
    includeHidden: boolean;
}

export interface FlushDailyTaskRequest {
    serviceId: string;
    dailyTaskId: string;
    plannedShiftOid: string;
    plannedShiftName: string;
    plannedShiftScheduledOid: string;
    plannedShiftScheduledName: string;
    scheduled_from_time: string;
    scheduled_to_time: string;
    dayOff: boolean;
    reassigned: boolean;
    assignmentError: boolean;
    overwrittenDailyMinutes: number;
    overwrittenBreakMinutes: number;
    plannedWorkDaySplitMinutes: number;
    plannedWorkdaySplitFromTime?: string;
    activities?: DailyTaskActivity[];
    plannedTips?: string[];
    plannedRatio: number;
    plannedMinutes?: number;
    absencePayload: AbsencePayloadItem[];
    copyWeekDays?: WeekDaysEnum[];
    copyUntilDate?: string;
    billingAutomationDisabled?: boolean;
    plannedBillingAutomation?: string;
    plannedOtherActivity?: GenericOtherActivity[];
    plannedWorkRatioActivity?: GenericWorkRatioActivity[];
    plannedActivityWorktime?: GenericActivityWorktime[];
    genericBillingAutomationGroup?: GenericBillingAutomationGroup[];
    overwriteExistingPlanning: boolean;
}

export interface RetrievePremisesHotelClosingConceptListRequest {
    marca_code: string;
    premisesId: string;
}

export interface FlushPremisesHotelClosingConceptRequest {
    marca_code: string;
    closingConcept: PremisesHotelClosingConcept;
}

export interface DeletePremisesHotelClosingConceptRequest {
    marca_code: string;
    closingConceptOid: string;
}

export interface GeneratePremisesHotelMagicCodeRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
}

export interface GeneratePremisesCenterMagicCodeRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
}

export interface RetrieveDailyTaskDataRequest {
    serviceId: string;
    premisesId: string;
    dailyTaskId: string;
    forceCreatePayload?: boolean;
}

export interface FlushDailyTaskDataRequest {
    serviceId: string;
    premisesId: string;
    dailyTaskId: string;
    validatorSelection: ValidatorSelectionEnum;
    customStartDailyTaskDate?: string;
    customEndDailyTaskDate?: string;
    customBreakMinutes?: number;
    customWorkDaySplitMinutes?: number;
    dayOff: boolean;
    reassigned: boolean;
    assignmentError: boolean;
    shiftOid?: string;
    shiftName?: string;
    tips: string[];
    absencePayload: AbsencePayloadItem[];
    forceSend2ConnectCore?: boolean;
    dailyTaskPayload: DailyTaskPayload;
}

export interface RegenerateDailyTaskDataRequest {
    serviceId: string;
    premisesId: string;
    dailyTaskId: string;
}

export interface RetrievePremisesWeeklyWorkRequest {
    serviceId: string;
    premisesId: string;
    dayOfWeek: string;
}

export interface CreateDocumentWeeklyWorkRequest {
    serviceId: string;
    premisesId: string;
    dayOfWeek: string;
}

export interface RetrievePremisesContactListRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
}

export interface RetrievePremisesGenericContactListRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
}

export interface FlushPremisesContactRequest {
    marca_code: string;
    contact: PremisesHotelContact;
}

export interface FlushPremisesGenericContactRequest {
    marca_code: string;
    contact: PremisesGenericContact;
}

export interface DeletePremisesContactRequest {
    marca_code: string;
    contactOid: string;
}

export interface DeletePremisesGenericContactRequest {
    marca_code: string;
    contactOid: string;
}

export interface RetrievePremisesWorkRequest {
    serviceId: string;
    premisesId: string;
    date: string;
}

export interface GenerateDocumentPremisesHotelWorkRequest {
    serviceId: string;
    premisesId: string;
    date: string;
}

export interface GeneratePremisesWorkRequest {
    serviceId: string;
    premisesId: string;
    date: string;
}

export interface SendPremisesHotelWorkRequest {
    serviceId: string;
    premisesId: string;
    date: string;
}

export interface FlushPremisesWorkClientRemarksRequest {
    serviceId: string;
    premisesId: string;
    date: string;
    clientRemarks: string;
}

export interface FlushPremisesHotelWorkCustomClosingConceptDataRequest {
    serviceId: string;
    premisesId: string;
    date: string;
    customClosingConceptData: CustomClosingConceptData[];
}

export interface RegenerateDailyTaskDataPeriodRequest {
    serviceId: string;
    premisesId: string;
    from: string;
    to: string;
}

export interface RetrievePremisesMonthlyWorkRequest {
    serviceId: string;
    premisesId: string;
    period: string;
    year: string;
}

export interface RetrievePremisesMonthlyWork4ClientRequest {
    calendarToken: string;
    period: string;
    year: string;
}

export interface SendDailyWork2ConnectCoreRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    userId: string;
    date: string;
    forceSend2ConnectCore: boolean;
}

export interface SendPremisesHotelWork2ConnectCoreRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    date: string;
    forceSend2ConnectCore: boolean;
}

export interface RetrieveBusTargetRatioRequest {
    marca_code: string;
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    update: boolean;
}

export interface RetrieveMonthlyTotalByNet4ConceptRequest {
    serviceId: string;
    premisesId: string;
    workplaceOid: string;
    period: string;
    year: string;
}

export interface RetrieveMonthlyTotalByNet4Concept4ClientRequest {
    calendarToken: string;
    period: string;
    year: string;
}

export interface RetrievePremisesLinkListReply extends JsonReply {
    premisesLink: PremisesLink[];
    size: number;
    count: number;
    eof: boolean;
}

export interface RetrievePremisesLinkReply extends JsonReply {
    premisesLink: PremisesLink;
    alertType: {
        activities: ConfigurationAlertType;
        catalog: ConfigurationAlertType;
        general: ConfigurationAlertType;
        occupancy: ConfigurationAlertType;
    }
}

export interface RetrievePremisesCenterGeneralReply extends JsonReply {
    general: PremisesCenterGeneral;
}

export interface RetrievePremisesHotelGeneralReply extends JsonReply {
    general: PremisesHotelGeneral;
}

export interface FlushPremisesCenterGeneralReply extends JsonReply {
    general: PremisesCenterGeneral;
}

export interface FlushPremisesHotelGeneralReply extends JsonReply {
    general: PremisesHotelGeneral;
}

export interface RetrievePremisesHotelOccupancyYearlyReply extends JsonReply {
    occupancyYearly: PremisesHotelOccupancyYearly;
}

export interface FlushPremisesHotelOccupancyYearlyReply extends JsonReply {
    occupancyYearly: PremisesHotelOccupancyYearly;
}

export interface RetrievePremisesHotelOccupancyYearlyEventsReply extends JsonReply {
    events: PremisesHotelOccupancyYearlyEvents[];
}

export interface FlushPremisesHotelOccupancyYearlyEventsReply extends JsonReply {
    event: PremisesHotelOccupancyYearlyEvents;
}

export interface RetrievePremisesHotelRatioReply extends JsonReply {
    roomRatios: PremisesHotelRatio[];
    commonAreaRatios: PremisesHotelRatio[];
}

export interface FlushPremisesHotelRatioReply extends JsonReply {
    ratio: PremisesHotelRatio;
}

export interface RetrievePremisesCenterNet4ConceptsReply extends JsonReply {
    net4Concepts: SearchNet4Concept[];
    retrievePremisesCenterActivityTypeListReply: RetrievePremisesCenterActivityTypeListReply;
}

export interface RetrievePremisesHotelNet4ConceptsReply extends JsonReply {
    net4Concepts: SearchNet4Concept[];
    retrievePremisesHotelActivityListReply: RetrievePremisesHotelActivityListReply;
}

export interface RetrievePremisesGenericNet4ConceptsReply extends JsonReply {
    net4Concepts: SearchNet4Concept[];
    activityTypes: PremisesGenericActivityType[];
}

export interface ClearPremisesGenericNet4ConceptsReply extends JsonReply {
    net4Concepts: SearchNet4Concept[];
    activityTypes: PremisesGenericActivityType[];
}

export interface SearchPremisesHotelNet4ConceptsReply extends JsonReply {
    net4Concepts: SearchNet4Concept[];
}

export interface SearchPremisesHotelActivityNet4ConceptsReply extends JsonReply {
    net4Concepts: SearchNet4Concept[];
}

export interface RetrievePremisesHotelRoomCatalogListReply extends JsonReply {
    roomCatalog: RoomType[];
    roomCatalogActivities4NewInstance: RoomActivity[];
}

export interface FlushPremisesHotelRoomCatalogReply extends JsonReply {
    roomCatalog: RoomType[];
}

export interface RetrievePremisesCenterActivityTypeListReply extends JsonReply {
    activities: PremisesCenterActivityType[];
}

export interface RetrievePremisesHotelActivityListReply extends JsonReply {
    activities: PremisesHotelActivity[];
}

export interface RetrievePremisesGenericActivityListReply extends JsonReply {
    activityTypes: PremisesGenericActivityType[];
}

export interface FlushPremisesGenericActivityTypeReply extends JsonReply {
    activityType: PremisesGenericActivityType;
}

export interface FlushPremisesHotelActivityReply extends JsonReply {
    activity: PremisesHotelActivity;
}

export interface RetrievePremisesHotelActivityGeneralReply extends JsonReply {
    activityGeneral: PremisesHotelActivityGeneral;
}

export interface FlushPremisesHotelActivityGeneralReply extends JsonReply {
    activityGeneral: PremisesHotelActivityGeneral;
}

export interface RetrievePremisesHotelTipListReply extends JsonReply {
    tips: PremisesHotelTip[];
}

export interface FlushPremisesHotelTipReply extends JsonReply {
    tip: PremisesHotelTip;
}

export interface RetrievePremisesHotelShiftGeneralReply extends JsonReply {
    shiftGeneral: PremisesHotelShiftGeneral;
}

export interface FlushPremisesHotelShiftGeneralReply extends JsonReply {
    shiftGeneral: PremisesHotelShiftGeneral;
}

export interface RetrievePremisesHotelShiftListReply extends JsonReply {
    shifts: PremisesShift[];
}

export interface RetrievePremisesGenericShiftListReply extends JsonReply {
    shifts: PremisesGenericShift[];
    activityTypeNames: string[];
    groupDisplayName: string;
    groupNames: string[];
}

export interface FlushPremisesHotelShiftReply extends JsonReply {
    shift: PremisesShift;
    schedules: PremisesShiftSchedule[];
}

export interface FlushPremisesGenericShiftReply extends JsonReply {
    shift: PremisesGenericShift;
    schedules: PremisesGenericShiftSchedule[];
}

export interface RetrievePremisesHotelShiftScheduleListReply extends JsonReply {
    schedules: PremisesShiftSchedule[];
}

export interface RetrievePremisesGenericShiftScheduleListReply extends JsonReply {
    schedules: PremisesGenericShiftSchedule[];
}

export interface RetrievePremisesHotelDailyWorkReply extends JsonReply {
    daily: PremisesHotelDaily;
    hotelWorkCurrentStatus: HotelWorkCurrentStatus;
    general: PremisesHotelGeneral;
    generalRatio: PremisesHotelGeneralRatio;
    tips: PremisesHotelTip[];
    activityGeneral: PremisesHotelActivityGeneral;
    shifts: PremisesShiftDailyWork[];
    activities: PremisesHotelActivity[];
    tasks: DailyTask[];
    absenceTypes: AbsenceType[];
    companyUsers: CompanyUser[];
    empresas: SelectOption[];
    magicBagMinutesMap: { [key: string]: number };
    size: number;
}

export interface RetrievePremisesCenterDailyWorkReply extends JsonReply {
    centerWorkCurrentStatus: CenterWorkCurrentStatus;
    bases: PremisesCenterBase[];
    shifts: PremisesShiftDailyWork[];
    activities: PremisesCenterActivityType[];
    tasks: DailyTask[];
    absenceTypes: AbsenceType[];
    companyUsers: CompanyUser[];
    empresas: SelectOption[];
    magicBagMinutesMap: { [key: string]: number };
    size: number;
}

export interface RetrievePremisesGenericDailyWorkReply extends JsonReply {
    genericWorkCurrentStatus: GenericWorkCurrentStatus;
    genericWork: PremisesGenericWork;
    groups: PremisesGenericGroups;
    shifts: PremisesShiftDailyWork[];
    activities: PremisesGenericActivityType[];
    tasks: DailyTask[];
    absenceTypes: AbsenceType[];
    companyUsers: CompanyUser[];
    empresas: SelectOption[];
    magicBagMinutesMap: { [key: string]: number };
    size: number;
}

export interface FlushPremisesHotelDailyOccupancyReply extends JsonReply {
    daily: PremisesHotelDaily;
}

export interface RetrievePremisesDailyWorkSearchOptionsReply extends JsonReply {
    premises: PremisesLink[];
}

export interface FlushDailyTaskReply extends JsonReply {
    dailyTask: DailyTask;
}

export interface RetrievePremisesHotelClosingConceptListReply extends JsonReply {
    closingConcept: PremisesHotelClosingConcept[];
}

export interface FlushPremisesHotelClosingConceptReply extends JsonReply {
    closingConcept: PremisesHotelClosingConcept;
}

export interface GeneratePremisesHotelMagicCodeReply extends JsonReply {
    magicCode: PremisesHotelMagicCode;
}

export interface GeneratePremisesCenterMagicCodeReply extends JsonReply {
    magicCode: PremisesCenterMagicCode;
}

export interface RetrieveDailyTaskDataReply extends JsonReply {
    task: DailyTask;
    workDayEvents: WorkDayEvent[];
}

export interface FlushDailyTaskDataReply extends JsonReply {
    dailyTask: DailyTask;
    sended2ConnectCore: boolean;
}

export interface RegenerateDailyTaskDataReply extends JsonReply {
    task: DailyTask;
    daily: PremisesHotelDaily;
}

export interface RetrievePremisesHotelWeeklyWorkReply extends JsonReply {
    dailySize: number;
    daily: PremisesHotelDaily[];
    hotelWorkCurrentStatus: HotelWorkCurrentStatus[];
    rowsSize: number;
    rows: WeeklyWorkSummaryRow[];
    general: PremisesHotelGeneral;
    generalRatio: PremisesHotelGeneralRatio;
    tips: PremisesHotelTip[];
    activityGeneral: PremisesHotelActivityGeneral;
    shifts: PremisesShift[];
    activities: PremisesHotelActivity[];
    absenceTypes: AbsenceType[];
    companyUsers: CompanyUser[];
    empresas: SelectOption[];
    magicBagMinutesMap: { [key: string]: number };
}

export interface RetrievePremisesCenterWeeklyWorkReply extends JsonReply {
    centerWorkCurrentStatus: CenterWorkCurrentStatus[];
    rowsSize: number;
    rows: WeeklyWorkSummaryRow[];
    bases: PremisesCenterBase[];
    shifts: PremisesShift[];
    activities: PremisesCenterActivityType[];
    absenceTypes: AbsenceType[];
    companyUsers: CompanyUser[];
    empresas: SelectOption[];
    magicBagMinutesMap: { [key: string]: number };
}

export interface RetrievePremisesGenericWeeklyWorkReply extends JsonReply {
    genericWorkCurrentStatus: GenericWorkCurrentStatus[];
    rowsSize: number;
    rows: WeeklyWorkSummaryRow[];
    groups: PremisesGenericGroups;
    shifts: PremisesShift[];
    activities: PremisesGenericActivityType[];
    absenceTypes: AbsenceType[];
    companyUsers: CompanyUser[];
    empresas: SelectOption[];
    magicBagMinutesMap: { [key: string]: number };
}

export interface RetrievePremisesHotelContactListReply extends JsonReply {
    contacts: PremisesHotelContact[];
}

export interface RetrievePremisesGenericContactListReply extends JsonReply {
    contacts: PremisesHotelContact[];
}

export interface FlushPremisesHotelContactReply extends JsonReply {
    contact: PremisesHotelContact;
}

export interface FlushPremisesGenericContactReply extends JsonReply {
    contact: PremisesGenericContact;
}

export interface RetrievePremisesCenterWorkReply extends JsonReply {
    centerWork: PremisesCenterWork;
    centerWorkStatus: PremisesCenterWorkStatus[];
    partaker: Partaker;
}

export interface RetrievePremisesGenericWorkReply extends JsonReply {
    genericWork: PremisesGenericWork;
    genericWorkStatus: PremisesGenericWorkStatus[];
    partaker: Partaker;
}

export interface RetrievePremisesHotelWorkReply extends JsonReply {
    hotelWork: PremisesHotelWork;
    hotelWorkStatus: PremisesHotelWorkStatus[];
    daily: PremisesHotelDaily;
    partaker: Partaker;
}

export interface GenerateDocumentPremisesHotelWorkReply extends JsonReply {
    hotelWork: PremisesHotelWork;
    hotelWorkStatus: PremisesHotelWorkStatus[];
    partaker: Partaker;
}

export interface GeneratePremisesCenterWorkReply extends JsonReply {
    centerWork: PremisesCenterWork;
    centerWorkStatus: PremisesCenterWorkStatus[];
    partaker: Partaker;
}

export interface GeneratePremisesCenterWorkReply extends JsonReply {
    genericWork: PremisesGenericWork;
    genericWorkStatus: PremisesGenericWorkStatus[];
    partaker: Partaker;
}

export interface GeneratePremisesHotelWorkReply extends JsonReply {
    hotelWork: PremisesHotelWork;
    hotelWorkStatus: PremisesHotelWorkStatus[];
    daily: PremisesHotelDaily;
    partaker: Partaker;
}

export interface SendPremisesCenterWorkReply extends JsonReply {
    centerWork: PremisesCenterWork;
    centerWorkStatus: PremisesCenterWorkStatus[];
    partaker: Partaker;
}

export interface SendPremisesGenericWorkReply extends JsonReply {
    genericWork: PremisesGenericWork;
    genericWorkStatus: PremisesGenericWorkStatus[];
    partaker: Partaker;
}

export interface SendPremisesHotelWorkReply extends JsonReply {
    hotelWork: PremisesHotelWork;
    hotelWorkStatus: PremisesHotelWorkStatus[];
    partaker: Partaker;
}

export interface FlushPremisesHotelWorkCustomClosingConceptDataReply extends JsonReply {
    hotelWork: PremisesHotelWork;
    hotelWorkStatus: PremisesHotelWorkStatus[];
    daily: PremisesHotelDaily;
    partaker: Partaker;
}

export interface RetrievePremisesCenterMonthlyWorkReply extends JsonReply {
    centerWorkCurrentStatus: CenterWorkCurrentStatus[];
}

export interface RetrievePremisesGenericMonthlyWorkReply extends JsonReply {
    genericWorkCurrentStatus: GenericWorkCurrentStatus[];
    groups: PremisesGenericGroups;
    activities: PremisesGenericActivityType[];
}

export interface RetrievePremisesHotelMonthlyWorkReply extends JsonReply {
    hotelWorkCurrentStatus: HotelWorkCurrentStatus[];
    general: PremisesHotelGeneral;
}

export interface RetrieveBusTargetRatioReply extends JsonReply {
    ratio: number;
    generalRatio: PremisesHotelGeneralRatio;
}

export interface RetrieveMonthlyTotalByNet4ConceptReply extends JsonReply {
    totalByNet4Concept: TotalByNet4Concept[];
}

export interface ValidateDailyTaskCustomRequest {
    serviceId: string;
    premisesId: string;
    dailyTaskId: string[];
    dayOff: boolean;
    reassigned: boolean;
    assignmentError: boolean;
    validated: boolean;
    customStartDailyTaskDate: string;
    customEndDailyTaskDate: string;
    customWorkDaySplitMinutes: number;
    customBreakMinutes: number;
    shiftOid: string;
    shiftName: string;
    tips: string[];
    absencePayload: AbsencePayloadItem[];
    billingAutomationDisabled?: boolean;
    billingAutomation?: string;
}

export interface ValidateDailyTaskPlannedRequest {
    serviceId: string;
    premisesId: string;
    dailyTaskId: string[];
    validated: boolean;
    plannedWorkDaySplitMinutes: number;
    plannedBreakMinutes: number;
    keepPausePeriods: boolean;
    billingAutomationDisabled?: boolean;
    billingAutomation?: string;
}

export interface ValidateDailyTaskWorkedRequest {
    serviceId: string;
    premisesId: string;
    dailyTaskId: string[];
    validated: boolean;
    workedWorkDaySplitMinutes: number;
    workedBreakMinutes: number;
    keepPausePeriods: boolean;
    billingAutomationDisabled?: boolean;
    billingAutomation?: string;
}

export interface ValidateDailyTaskReply extends JsonReply {
    workedHours: DailyTaskWorkedHours[];
}

export interface SyncMagicBagMinutesRequest {
    serviceId: string;
    premisesId: string;
    userId: string;
    date?: string;
}

export interface SyncMagicBagMinutesReply extends JsonReply {
    magicBagMinutes: number;
}

export interface DailyTaskWorkedHours {
    serviceId: string;
    premisesId: string;
    dailyTaskId: string;
    userId: string;
    userSAMAccountName: string;
    displayName: string;
    userJob: string;
    weeklyHours: number;
    workedHoursStatus: DailyTaskWorkedHoursStatusEnum;
    
    dayOff: boolean;
    reassigned: boolean;
    assignmentError: boolean;
    
    // Planned
    plannedShiftName: string;
    plannedShiftScheduledName: string;
    plannedWorkDaySplit: boolean;
    plannedIncludesBreakTime: boolean;
    plannedStartDailyTaskDate: string;
    plannedEndDailyTaskDate: string;
    plannedBreakMinutes: number;
    
    // Worked
    shiftName: string;
    workedScheduledStr: string;
    workedStartDailyTaskDate: string;
    workedEndDailyTaskDate: string;
    workedBreakMinutes: number;
    
    // Custom
    customStartDailyTaskDate: string
    customEndDailyTaskDate: string;
    customBreakMinutes: number;
    
    validatorSelection: ValidatorSelectionEnum;
    statusInConnectCore: ConnectCoreDailyWorkStatusEnum;
    alertType: ConfigurationAlertType;
    alert: ConfigurationAlert[];
    absentAllDay: boolean;
    plannedAbsence: PlannedAbsence; 
    absencePayload: AbsencePayloadItem[];
}

export interface RetrievePremisesGenericGroupsRequest {
    marca_code: string;
    premisesId: string;
}

export interface RetrievePremisesGenericGroupsReply extends JsonReply {
    groups: PremisesGenericGroups;
}

export interface FlushPremisesGenericGroupsRequest {
    marca_code: string;
    groups: PremisesGenericGroups;
}

export interface FlushPremisesGenericGroupsReply extends JsonReply {
    groups: PremisesGenericGroups;
}

export interface PremisesDailyDemand {
    dailyDemandId?: string;
    net4ConceptId: number;
    weekDays: WeekDaysEnum[];
    alternativeName: string | undefined;
    fromTime: string;
    toTime: string;
    type: PremisesDailyDemandTypeEnum | undefined;
    goal: number | undefined;
    minGoal: number | undefined;
    breakMinutes: number | undefined;
    workdaySplitMinutes: number | undefined;
    workdaySplitFromTime: string;
}

export enum PremisesDailyDemandTypeEnum {
    PEOPLE = 'PEOPLE',
    TIME = 'TIME',
    UNITS = 'UNITS'
}

export enum PremisesDemandPeriodEnum {
    DAILY = 'DAILY',
    WITHOUT_DEMAND = 'WITHOUT_DEMAND'
}

export interface FlushDailyTaskActivityDataRequest {
    serviceId: string;
    premisesId: string;
    dailyTaskPayload: {
        [dailyTaskId: string]: DailyTaskPayload;
    }
}

export interface FlushDailyTaskActivityDataReply extends JsonReply {
    dailyTaskPayload: {
        [dailyTaskId: string]: DailyTaskPayload;
    }
}

export interface ValidateDailyTaskCustomReducedRequest {
    serviceId: string;
    premisesId: string;
    dailyTaskId: string;
    customStartDailyTaskDate: string;
    customEndDailyTaskDate: string;
    customWorkDaySplitMinutes?: number;
    customBreakMinutes?: number;
}

export interface ValidateDailyTaskCustomReducedReply extends JsonReply {
    dailyTask: DailyTask;
}

export interface ValidateDailyTaskCustomReducedListRequest {
    serviceId: string;
    premisesId: string;
    dailyTaskId: string[];
    overwrite: boolean;
    customWorkDaySplitMinutes?: number;
    customBreakMinutes?: number;
}

export interface ValidateDailyTaskCustomReducedListReply extends JsonReply {
    dailyTask: DailyTask[];
}


export interface WorkplaceBillingAutomationConfig {
    serviceId: string;
    workplaceCode: string;
    year: string;
    holidayNightHoursAdjustmentMode: HolidayNightHoursAdjustmentModeEnum;
    holidayDayHoursAsNightHours: boolean;
    billingTimeRecordFallbackPolicy: BillingTimeRecordFallbackPolicyEnum;
    nocturnalFromTime: string;
    nocturnalFromTime2: string;
    nocturnalToTime: string;
    nocturnalToTime2: string;
    paidRestPolicy: PaidRestPolicyEnum;
    weekDaysAsHoliday: WeekDaysEnum[];
    holidayDates: string[];
    dayStartTime?: string;
    precalculateManualSchedule: boolean;
}
export interface RetrievePremisesWorkplaceBillingAutomationConfigRequest {
    serviceId: string;
    workplaceCode: string;
    year: string;
}

export interface RetrievePremisesWorkplaceBillingAutomationConfigReply extends JsonReply {
    workplaceBillingAutomationConfig: WorkplaceBillingAutomationConfig;
}

export interface FlushDailyTaskListRequest {
    serviceId: string;
    premisesId: string;
    dailyTaskId: string[];
    plannedShiftOid: string;
    plannedShiftName: string;
    plannedShiftScheduledOid: string;
    plannedShiftScheduledName: string;
    scheduled_from_time: string;
    scheduled_to_time: string;
    overwrittenDailyMinutes: number;
    overwrittenBreakMinutes: number;
    unpaidRestAdjustment: UnpaidRestAdjustmentEnum;
    plannedWorkDaySplitMinutes: number;
    plannedWorkdaySplitFromTime: string;
    billingAutomationDisabled: boolean;
    plannedBillingAutomation: string;
    copyWeekDays?: WeekDaysEnum[];
    copyUntilDate?: string;
    overwriteExistingPlanning: boolean;
    dayOff: boolean;
    reassigned: boolean;
    assignmentError: boolean;
    absencePayload: AbsencePayloadItem[];
}

export interface FlushDailyTaskPlannedActivityDataRequest {
    serviceId: string;
    premisesId: string;
    overwriteExistingPlanning: boolean;
    plannedShiftOid?: string;
    plannedShiftName?: string;
    plannedShiftScheduledOid?: string;
    plannedShiftScheduledName?: string;
    plannedWorkDaySplitMinutes?: number;
    plannedWorkdaySplitFromTime?: string;
    scheduled_from_time?: string;
    scheduled_to_time?: string;
    overwrittenDailyMinutes?: number;
    overwrittenBreakMinutes?: number;
    unpaidRestAdjustment?: UnpaidRestAdjustmentEnum;
    dailyTaskPayload: {
        [dailyTaskId: string]: DailyTaskPayload;
    };
    copyWeekDays?: WeekDaysEnum[];
    copyUntilDate?: string;
}

export interface FlushDailyTaskPlannedActivityDataReply extends JsonReply {
    dailyTaskPayload: {
        [dailyTaskId: string]: DailyTaskPayload;
    }
}

export interface CopyDailyTaskPlannedActivitiesRequest {
    serviceId: string;
    premisesId: string;
    dailyTaskId: string[];
}

export interface RetrievePremisesGenericActivityTypeObjectiveListRequest {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    date: string;
    all?: boolean;
    billingAutomation?: string;
    net4ConceptId?: number;
    net4ConceptAlternativeName?: string;
    includeDeprecated: boolean;
}

export interface RetrievePremisesGenericActivityTypeObjectiveListReply extends JsonReply {
    objectives: PremisesGenericActivityTypeObjective[];
}

export interface FlushPremisesGenericActivityTypeObjectiveRequest {
    objective: PremisesGenericActivityTypeObjective;
}

export interface FlushPremisesGenericActivityTypeObjectiveReply extends JsonReply {
    objective: PremisesGenericActivityTypeObjective;
}

export interface DeletePremisesGenericActivityTypeObjectiveRequest {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    objectiveId: string;
}

export interface DownloadWorkRealizedExcelRequest {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    year: string;
    period: string;
    writeTimeAsDecimal: boolean;
}

export interface UploadWorkRealizedExcelRequest {
    excelType: ExcelTypeEnum;
    async: boolean;
    file: string;
    payload: UploadWorkRealizedExcelPayload;
}

export interface UploadWorkRealizedExcelPayload {
    premisesId: string;
    serviceId: string;
    workplaceOid: string;
    year: string;
    period: string;
}

export interface UploadWorkRealizedExcelReply extends JsonReply {
    errors?: boolean;
    async?: boolean;
    forcedAsync?: boolean;
    file?: string;
}

export interface CopyDailyTaskPlanningToFutureRequest {
    serviceId: string;
    premisesId: string;
    workplaceOid: string;
    dailyTaskId: string[];
    copyWeekDays: WeekDaysEnum[];
    copyUntilDate: string;
    overwriteExistingPlanning: boolean;
}

export enum HolidayNightHoursAdjustmentModeEnum {
    ENTRY_DATE = 'ENTRY_DATE',
    EXIT_DATE = 'EXIT_DATE',
    CALENDAR_ADJUSTED = 'CALENDAR_ADJUSTED'
}

export enum BillingTimeRecordFallbackPolicyEnum {
    USE_PLANNED_FIRST = 'USE_PLANNED_FIRST',
    USE_WORKED_FIRST = 'USE_WORKED_FIRST',
    NEVER_USE_PLANNED = 'NEVER_USE_PLANNED',
    NEVER_USE_WORKED = 'NEVER_USE_WORKED'
}

export enum PaidRestPolicyEnum {
    ENABLED = 'ENABLED',
    DISABLED = 'DISABLED',
    USE_PAYROLL_CONFIG = 'USE_PAYROLL_CONFIG'
}

export enum UnpaidRestAdjustmentEnum {
    NONE = 'NONE',
    ADVANCE_START_TIME = 'ADVANCE_START_TIME',
    DELAY_END_TIME = 'DELAY_END_TIME'
}

export enum ExcelTypeEnum {
    CENTER_REAL_CONCEPT = 'CENTER_REAL_CONCEPT',
    GOALS = 'GOALS',
    RESOURCES = 'RESOURCES',
    TASKS = 'TASKS',
    LOCATION_MASTER = 'LOCATION_MASTER',
    GENERAL_MASTER = 'GENERAL_MASTER'
}